import { DataType, Form, Section, SelectOption, useNotify } from '@globaleris/ui.library'
import { FormOptions } from '@globaleris/ui.library/src/lib'
import { Button, Card, Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCurrency } from '../../../api'
import { EnumCommodity } from '../../../api/Argentia/enums'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import { EnumIdioma } from '../../../api/Argentia/enums/enumIdioma'
import { EnumProrrateo } from '../../../api/Argentia/enums/enumProrrateo'
import { LanguageModel, ProductModel } from '../../../api/Argentia/model/contracts'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import {
  enumEnumToValueNumber,
  enumKeyToValue,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../helpers'
import { getFilteredBreakdownOptions } from '../../../helpers/breakdownOptionsHelper'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { NonAuthRoutes } from '../../../model'
import { getActiveCommodities, getCustomerDomain, notifyError } from '../../../redux'
import TemplateSettings from '../../templateSettings/templateSettings.model'
import { ProductModelResponse } from '../products.Model'
import { addProductToTemplateSettings, filterSelectedTemplates } from '../products.helpers'
import { mapTemplateToSelectOption, mapTemplates } from '../products.mapper'
import {
  createProduct,
  fetchLanguages,
  fetchProductTypeOptionsByClientType,
  fetchTemplates,
  fetchWeightedTypeListByBreakdownType,
  postTemplateUpdate
} from '../products.service'
import { mapDefaultLanguage, mapLanguagesResponse } from './products.add.mapper'

export interface ProductsPageProps {
  location: { state: { clientType: EnumClientTypes } }
}

export const ProductsAddPage: React.FC<ProductsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'products', 'enums'])
  const history = useHistory()
  const clientType = props?.location?.state?.clientType ?? history.goBack()
  const commodities = getActiveCommodities()

  const { info, error } = useNotify()

  const [product, setProduct] = useState<ProductModel>({
    commodity:
      commodities?.length === 1 ? enumEnumToValueNumber(EnumCommodity, commodities[0]) : undefined
  } as ProductModel)

  const [isFetchingProductTypes, setIsFetchingProductTypes] = useState(false)
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false)
  const [productTypeOptions, setProductTypeOptions] = useState<SelectOption[] | undefined>([])
  const [languages, setLanguages] = useState<LanguageModel[]>([])
  const [templates, setTemplates] = useState<TemplateSettings[]>([])
  const [templatesOption, setTemplatesOptions] = useState<SelectOption[] | undefined>([])
  const [templateConceptsOption, setTemplateConceptsOptions] = useState<SelectOption[] | undefined>(
    []
  )
  const [weightedTypeOptions, setWeightedTypeOptions] = useState<SelectOption[] | undefined>([])
  const [isFetchingWeightedType, setIsFetchingWeightedType] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [breakdownOptions, setBreakdownOptions] = useState<SelectOption[] | undefined>([])

  // Handlers Starts

  const handleSaveBack = async (data): Promise<Boolean> => {
    setSaveLoading(true)
    if (missingFieldsForm(addFormSections, data)) {
      error(t('products:forms.add.notifications.save.errorInvalidForm'))
      return false
    } else {
      // add default translation & let continue with error
      const newData = addDefaultTranslation(data)
      if (newData.translations?.length === 0)
        error(t('products:forms.add.notifications.save.errorDefaultLanguage'))

      // create product with default translation
      await createProduct(newData).then(response => {
        if (response.success) {
          info(t('products:forms.add.notifications.save.success'))
          setSaveLoading(false)
          history.push(NonAuthRoutes.Products, { clientType: clientType })
        } else {
          error(t('products:forms.add.notifications.save.error'))
          setSaveLoading(false)
        }
      })
      return true
    }
  }

  const handleSaveAndRedirect = async (data, redirect): Promise<Boolean> => {
    setSaveLoading(true)
    let newProduct: ProductModelResponse | undefined = undefined // declare here to have function-block scope

    if (missingFieldsForm(addFormSections, data)) {
      error(t('products:forms.add.notifications.save.errorInvalidForm'))
      return false
    } else {
      // add default translation & let continue with error
      const newData = addDefaultTranslation(data)
      if (newData.translations?.length === 0)
        error(t('products:forms.add.notifications.save.errorDefaultLanguage'))

      // create product with default translation
      await createProduct(newData).then(async response => {
        if (response.success) {
          newProduct = response.data
          info(t('products:forms.add.notifications.save.success'))

          if (newProduct != undefined) {
            // Update product templates if createProduct was successfull
            if (data.templates?.length > 0 || data.templateConcepts?.length > 0) {
              // update templates if input was used
              const templateSettings: TemplateSettings[] | undefined = filterSelectedTemplates(
                templates,
                [...(data.templates || []), ...(data.templateConcepts || [])]
              )

              const templateSettingsUpdated = addProductToTemplateSettings(
                templateSettings,
                newProduct.id,
                newProduct.description
              )
              const success: boolean[] = [] //array made to store all post status responses

              await Promise.all(
                // POST each Selected Template with newProduct Id received from createProduct call
                templateSettingsUpdated.map(async template => {
                  await postTemplateUpdate(template).then(response => {
                    if (response.success) {
                      success.push(true)
                    } else {
                      success.push(false)
                    }
                  })
                })
              ).finally(() => {
                // if (success.includes({success: false}))
                //incorporar find o allSettled
                if (success.includes(false)) {
                  error(t('products:forms.add.notifications.save.errorTemplates'))
                } else {
                  info(t('products:forms.add.notifications.save.successTemplates'))
                }
                setSaveLoading(false)
                redirect(newProduct)
              })
            } else {
              redirect(newProduct)
            }
          }
        } else {
          error(t('products:forms.add.notifications.save.error'))
          setSaveLoading(false)
        }
      })

      return true
    }
  }

  // Handlers Ends

  // Form Definition Starts

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('products:forms.add.analytics')
    }
  }

  const addFormSections: Section[] = [
    {
      title: t('products:forms.add.title'),
      inputs: [
        {
          name: 'code',
          label: t('products:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'description',
          label: t('products:forms.add.inputs.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'commodity',
          label: t('products:forms.add.inputs.commodity'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          readOnly: commodities?.length === 1,
          clearable: true,
          onChange: value => {
            const num = Number(value)
            const commodityValue =
              value === '' || value == null || Number.isNaN(num)
                ? undefined
                : (num as EnumCommodity)
            setBreakdownOptions(getFilteredBreakdownOptions(commodityValue))
          }
        },
        {
          name: 'type',
          label: t('products:forms.add.inputs.type'),
          type: DataType.SELECT,
          isLoading: isFetchingProductTypes,
          selectOptions: productTypeOptions,
          required: true
        },
        {
          name: 'currency',
          label: t('products:forms.add.inputs.currency'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCurrency, 'enums:EnumCurrencyLabel'),
          required: true
        },
        {
          name: 'breakdownType',
          label: t('products:forms.add.inputs.breakdownType'),
          type: DataType.SELECT,
          required: true,
          selectOptions: breakdownOptions,
          clearable: true,
          onChange: (value, formState) => {
            setWeightedTypeOptions([])
            const breakdownTypeId = +value
            breakdownTypeId && fetchWeightedTypeOptionList(breakdownTypeId)
          }
        },
        {
          name: 'weightedType',
          label: t('products:forms.add.inputs.weightedType'),
          type: DataType.SELECT,
          required: true,
          selectOptions: weightedTypeOptions,
          isLoading: isFetchingWeightedType,
          clearable: true
        },
        {
          name: 'templates',
          label: t('products:forms.add.inputs.templates'),
          type: DataType.MULTISELECT,
          selectOptions: templatesOption,
          clearable: true,
          isLoading: isFetchingTemplates
        },
        {
          name: 'templateConcepts',
          label: t('products:forms.add.inputs.templateConcepts'),
          type: DataType.MULTISELECT,
          selectOptions: templateConceptsOption,
          clearable: true,
          isLoading: isFetchingTemplates
        }
      ]
    }
  ]

  // Form Definition Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts

  const addDefaultTranslation = (data: ProductModel): ProductModel => {
    // this function adds a default translation to product before POST

    const defaultLanguage = mapDefaultLanguage(languages)
    if (defaultLanguage == undefined) {
      error(t('products:forms.add.notifications.load.errorDefaultLanguage'))
    } else {
      return {
        ...data,
        translations: [
          {
            language: defaultLanguage.id ? defaultLanguage.id : enumKeyToValue(EnumIdioma, 'ES'),
            description: data.description,
            default: true
          }
        ]
      }
    }
    return data
  }

  // Functions Ends

  // Hooks Starts

  useEffect(() => {
    const fetchProductTypeOptions = async (clientType: EnumClientTypes) => {
      setIsFetchingProductTypes(true)
      setProductTypeOptions([])
      await fetchProductTypeOptionsByClientType(clientType)
        .then(response => {
          if (response.success) {
            setProductTypeOptions(response.data)
          } else {
            notifyError(t('products:notifications.FetchProductType.error'))
          }
        })
        .finally(() => {
          setIsFetchingProductTypes(false)
        })
    }
    fetchProductTypeOptions(clientType)

    // Languagues Fetch
    const getLanguages = async () => {
      await fetchLanguages().then(response => {
        if (response.success) {
          setLanguages(mapLanguagesResponse(response.data))
        } else {
          error(t('products:forms.add.notifications.load.errorFetchingLanguages'))
          return []
        }
      })
    }
    getLanguages()

    // Templates Fetch
    const getTemplates = async () => {
      setIsFetchingTemplates(true)
      await fetchTemplates()
        .then(response => {
          if (response.success) {
            setTemplates(response.data as TemplateSettings[]) // handle all template data
            const { templatesOnly, templateConceptsOnly } = mapTemplates(response.data) // filter template data from usecase (templates & Concepts only)
            setTemplatesOptions(mapTemplateToSelectOption(templatesOnly)) // map template data to Form SelectOption
            setTemplateConceptsOptions(mapTemplateToSelectOption(templateConceptsOnly)) // map template data to Form SelectOption
          } else {
            error(t('products:forms.add.notifications.load.errorGetTemplates'))
          }
          return []
        })
        .finally(() => {
          setIsFetchingTemplates(false)
        })
    }
    getTemplates()
  }, [])

  useEffect(() => {
    setBreakdownOptions(getFilteredBreakdownOptions(product?.commodity))
  }, [product?.commodity])

  // weightedTypeFetch
  const fetchWeightedTypeOptionList = async (breakdownTypeId: number) => {
    setIsFetchingWeightedType(true)
    await fetchWeightedTypeListByBreakdownType(breakdownTypeId)
      .then(response => {
        if (response.success && response.data) {
          setWeightedTypeOptions(
            enumToSelectOptionArrayDefaultValues(
              EnumProrrateo,
              'enums:EnumProrrateo',
              response.data
            )
          )
        } else {
          error(t('products:forms.add.notifications.load.errorWeightedType'))
        }
      })
      .finally(() => setIsFetchingWeightedType(false))
  }

  // Hooks Ends

  return (
    <>
      {clientType != undefined && (
        <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={false}>
          <PageHeader title={t('products:forms.add.title')}>
            <div>
              <Grid container direction="row" justify="flex-end" spacing={2}>
                <Grid item>
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    // disabled={isFetching}
                    onClick={() => {
                      history.goBack()
                    }}
                  >
                    {t('products:mainActions.back')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    // disabled={isFetching}
                    onClick={() => {
                      handleSaveAndRedirect(product, () => {
                        history.push(NonAuthRoutes.Products, { clientType: clientType })
                      })
                    }}
                  >
                    {t('products:mainActions.saveBack')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    // disabled={isFetching}
                    onClick={() => {
                      handleSaveAndRedirect(product, newProduct => {
                        history.push(NonAuthRoutes.ProductsEdit, {
                          productId: newProduct?.id,
                          clientType: clientType
                        })
                      })
                    }}
                  >
                    {t('products:mainActions.saveContinue')}
                  </Button>
                </Grid>
              </Grid>
            </div>
          </PageHeader>
          <Card raised>
            <Form sections={addFormSections} data={product} options={options} />
          </Card>
        </Page>
      )}
    </>
  )
}
